// src/features/kommunikation/components/SonderversandModal.tsx
//
// Sonderversand: außerplanmäßiger Versand von Gehaltsunterlagen an einen
// einzelnen Arbeitnehmer, unabhängig vom Monatsschema. Der Dateiname wird
// lenient geparst (nur Mandantennummer + Personalnummer nötig), Monat/Jahr
// bleiben in der DB leer (ist_sonderversand = true), der Versand erfolgt sofort.
//
// Schwester-Komponente von DocumentUploadModal, bewusst eigenständig gehalten,
// damit der reguläre Monatsversand vollständig unberührt bleibt.

import React, { useState } from 'react';
import { FileText, Upload, CheckCircle2, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../../auth';
import { Customer } from '../../../shared/types';
import { ParsedSonderversand } from '../types/upload.types';
import { customerService } from '../../../services/customer/CustomerService';
import { CustomerSelectionModal } from './CustomerSelectionModal';

import { documentParserService } from '../services/DocumentParserService';
import { documentStorageService } from '../services/DocumentStorageService';
import { communicationDocumentService } from '../services/CommunicationDocumentService';
import { credentialsService } from '../services/CredentialsService';

interface SonderversandModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

type SonderversandStatus =
  | 'idle'
  | 'pending'
  | 'needs_customer'
  | 'no_credential'
  | 'error'
  | 'sending'
  | 'success';

export const SonderversandModal: React.FC<SonderversandModalProps> = ({
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedSonderversand | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [hasCredential, setHasCredential] = useState(false);
  const [passwort, setPasswort] = useState('');
  const [credentialEmail, setCredentialEmail] = useState('');
  const [status, setStatus] = useState<SonderversandStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);

  const [customerSelection, setCustomerSelection] = useState<{
    isOpen: boolean;
    customers: Customer[];
    filename: string;
  } | null>(null);

  const resetState = () => {
    setFile(null);
    setParsed(null);
    setCustomer(null);
    setHasCredential(false);
    setPasswort('');
    setCredentialEmail('');
    setStatus('idle');
    setError(null);
    setCustomerSelection(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  /**
   * Löst das Credential für einen konkreten Kunden + Parse-Ergebnis auf.
   */
  const resolveCredential = async (cust: Customer, p: ParsedSonderversand) => {
    if (!cust.beraternummer) {
      setCustomer(cust);
      setHasCredential(false);
      setPasswort('');
      setCredentialEmail('');
      setStatus('error');
      setError('Kunde hat keine Beraternummer');
      return;
    }

    const credential = await credentialsService.findCredential({
      beraternummer: cust.beraternummer,
      mandantennummer: p.mandantennummer,
      personalnummer: p.personalnummer
    });

    if (!credential) {
      setCustomer(cust);
      setHasCredential(false);
      setPasswort('');
      setCredentialEmail('');
      setStatus('no_credential');
      setError(`Kein Passwort für Personal-Nr. ${p.personalnummer} hinterlegt`);
      return;
    }

    setCustomer(cust);
    setHasCredential(true);
    setPasswort(credential.passwort);
    setCredentialEmail(credential.email);
    setStatus('pending');
    setError(null);
  };

  /**
   * Datei ausgewählt: lenient parsen, Kunde + Credential ermitteln.
   */
  const handleFileSelected = async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const selected = selectedFiles[0];

    setParsing(true);
    setError(null);

    try {
      const p = documentParserService.parseFilenameSonderversand(selected.name);
      setFile(selected);
      setParsed(p);
      setCustomer(null);
      setHasCredential(false);
      setPasswort('');
      setCredentialEmail('');

      if (!p.isValid) {
        setStatus('error');
        setError(p.error || 'Ungültiger Dateiname');
        return;
      }

      const customers = await customerService.findCustomersByMandantennummer(p.mandantennummer);

      if (customers.length === 0) {
        setStatus('error');
        setError(`Kein Kunde mit Mandantennummer ${p.mandantennummer} gefunden`);
        return;
      }

      if (customers.length > 1) {
        setStatus('needs_customer');
        setCustomerSelection({ isOpen: true, customers, filename: selected.name });
        return;
      }

      await resolveCredential(customers[0], p);
    } finally {
      setParsing(false);
    }
  };

  const handleCustomerSelected = async (cust: Customer) => {
    setCustomerSelection(null);
    if (parsed) {
      await resolveCredential(cust, parsed);
    }
  };

  /**
   * Führt Upload + DB-Eintrag + sofortigen Versand aus.
   */
  const handleSend = async () => {
    if (!file || !parsed || !parsed.isValid || !hasCredential || !customer?.beraternummer) {
      return;
    }

    setStatus('sending');
    setError(null);

    try {
      // 1. Upload zu Storage (Pfad ohne Monat/Jahr)
      const storageResult = await documentStorageService.uploadSonderversandDocument(
        file,
        parsed.mandantennummer,
        parsed.personalnummer
      );

      if (!storageResult.success || !storageResult.storagePath) {
        throw new Error(storageResult.error || 'Storage-Upload fehlgeschlagen');
      }

      // 2. DB-Eintrag (Monat/Jahr null, ist_sonderversand true)
      const dbResult = await communicationDocumentService.uploadSonderversandComplete(
        parsed.mandantennummer,
        parsed.personalnummer,
        file.name,
        storageResult.storagePath,
        file.size,
        passwort,
        user?.email || 'unknown'
      );

      if (!dbResult.success || !dbResult.documentId) {
        throw new Error(dbResult.error || 'Datenbank-Speicherung fehlgeschlagen');
      }

      // 3. Sofort versenden
      const sendResult = await communicationDocumentService.sendDocumentNow(dbResult.documentId);

      if (!sendResult.success) {
        throw new Error(sendResult.error || 'Versand fehlgeschlagen');
      }

      setStatus('success');
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 2000);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Sonderversand fehlgeschlagen');
    }
  };

  const canSend =
    !!file &&
    !!parsed &&
    parsed.isValid &&
    hasCredential &&
    !!customer?.beraternummer &&
    status === 'pending';

  const busy = parsing || status === 'sending' || status === 'success';

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '720px',
            width: '90%',
            maxHeight: '85vh',
            overflow: 'auto',
            border: '1px solid #E5E7EB',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '8px',
              backgroundColor: '#8B5CF615', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FileText size={18} color="#8B5CF6" />
            </div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' }}>
              Sonderversand
            </h2>
          </div>

          {/* Info Box */}
          <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '8px', fontSize: '14px' }}>
            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#5B21B6', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FileText size={15} color="#8B5CF6" /> Außerplanmäßiger Versand
            </div>
            <div style={{ marginBottom: '10px', fontSize: '13px', color: '#5B21B6' }}>
              Für Unterlagen, die nicht dem Monatsschema entsprechen (z. B. eine Sammlung aller Gehaltsunterlagen eines Arbeitnehmers). Der Versand erfolgt sofort an den hinterlegten Empfänger.
            </div>
            <div style={{ fontWeight: '600', marginBottom: '4px', color: '#5B21B6' }}>Dateiname-Format:</div>
            <code style={{ display: 'block', backgroundColor: 'white', padding: '8px', borderRadius: '6px', fontSize: '12px', border: '1px solid #E5E7EB' }}>
              Mandantennummer_Personalnummer_BeliebigerRest.pdf
            </code>
            <div style={{ marginTop: '8px', fontSize: '13px', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CheckCircle2 size={14} color="#059669" style={{ flexShrink: 0 }} />
              <span>Empfänger und Passwort werden über Mandant + Personalnummer automatisch ermittelt</span>
            </div>
          </div>

          {/* File Upload */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                padding: '24px',
                border: '2px dashed #D1D5DB',
                borderRadius: '10px',
                textAlign: 'center',
                cursor: busy ? 'not-allowed' : 'pointer',
                backgroundColor: busy ? '#F3F4F6' : '#F9FAFB',
                opacity: busy ? 0.6 : 1
              }}
            >
              <input
                type="file"
                accept=".pdf"
                disabled={busy}
                onChange={(e) => handleFileSelected(e.target.files)}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <Upload size={36} color="#9CA3AF" />
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
                PDF auswählen
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                Eine Datei für einen Arbeitnehmer
              </div>
            </label>
          </div>

          {/* Parsing Loading */}
          {parsing && (
            <div style={{
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'center',
              color: '#6B7280',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <Loader2 size={28} color="#9CA3AF" />
              </div>
              <div style={{ fontWeight: '600' }}>Datei wird analysiert...</div>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div style={{
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#ECFDF5',
              borderRadius: '8px',
              border: '1px solid #6EE7B7'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle2 size={24} color="#059669" />
                <div>
                  <div style={{ fontWeight: '600', color: '#065F46' }}>Sonderversand erfolgreich!</div>
                  <div style={{ fontSize: '14px', color: '#047857' }}>Die Unterlagen wurden versendet</div>
                </div>
              </div>
            </div>
          )}

          {/* Sending */}
          {status === 'sending' && (
            <div style={{
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#EFF6FF',
              borderRadius: '8px',
              border: '1px solid #93C5FD'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Send size={22} color="#3B82F6" />
                <div>
                  <div style={{ fontWeight: '600', color: '#1E40AF' }}>Wird versendet...</div>
                  <div style={{ fontSize: '14px', color: '#3B82F6' }}>Bitte warten</div>
                </div>
              </div>
            </div>
          )}

          {/* Datei-Info */}
          {file && status !== 'success' && (
            <div
              style={{
                padding: '16px',
                marginBottom: '16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                backgroundColor:
                  status === 'error' || status === 'no_credential'
                    ? '#FEF2F2'
                    : status === 'pending'
                    ? '#F0FDF4'
                    : 'white'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <FileText size={22} color="#6B7280" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>{file.name}</div>
                  {error && (
                    <div style={{ fontSize: '12px', color: '#991B1B' }}>{error}</div>
                  )}
                  {!error && status === 'pending' && (
                    <div style={{ fontSize: '12px', color: '#059669' }}>Bereit zum Versand</div>
                  )}
                </div>
              </div>

              {parsed?.isValid && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '8px',
                  fontSize: '12px',
                  padding: '10px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '6px'
                }}>
                  <div><strong>Mandant:</strong> {parsed.mandantennummer}</div>
                  <div><strong>Personal-Nr:</strong> {parsed.personalnummer}</div>
                  {customer && (
                    <>
                      <div><strong>Kunde:</strong> {customer.company}</div>
                      <div><strong>Berater-Nr:</strong> {customer.beraternummer}</div>
                    </>
                  )}
                  {hasCredential && credentialEmail && (
                    <div style={{ gridColumn: '1 / -1', color: '#059669', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle2 size={14} color="#059669" /> Empfänger: {credentialEmail}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleSend}
              disabled={!canSend}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: canSend ? '#8B5CF6' : '#D1D5DB',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: canSend ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Send size={16} />
              {status === 'sending' ? 'Wird versendet...' : 'Jetzt versenden'}
            </button>

            <button
              onClick={handleClose}
              disabled={status === 'sending' || status === 'success'}
              style={{
                padding: '12px 24px',
                backgroundColor: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                cursor: (status === 'sending' || status === 'success') ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151'
              }}
            >
              {(status === 'sending' || status === 'success') ? 'Bitte warten...' : 'Schließen'}
            </button>
          </div>
        </div>
      </div>

      {/* Customer Selection Modal */}
      {customerSelection && (
        <CustomerSelectionModal
          isOpen={customerSelection.isOpen}
          customers={customerSelection.customers}
          filename={customerSelection.filename}
          onSelect={handleCustomerSelected}
          onCancel={() => setCustomerSelection(null)}
        />
      )}
    </>
  );
};
